import React from 'react';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { RouterError } from '../../../../bootstrap/routerHelpers/RouterError';
import { SessionManagementService } from '../../../../sessionManagement/SessionManagementService';
import { Sites } from '../../../../siteManagement/services/resources/Sites';
import { GeneralSiteSettingsCoordinator } from '../../../general-settings/components/site-settings/GeneralSiteSettingsCoordinator';
import { IEditableServerSettings } from '../../../ServerApi/types/IEditableServerSettings';
import { ServerSettings } from '../../../services/ServerSettings';
import {
  ISiteAuthenticationSettings,
  SiteAuthenticationSettingsService,
} from '../../../siteAuthSettings/services/SiteAuthenticationSettingsService';
import { ISiteSettingsForServerAdmin } from '../../../types/ISite';
import { ISiteSettingsForSiteAdmin } from '../../../types/ISiteSettingsForSiteAdmin';

export interface GeneralSiteSettingsSubspaceOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly server: SessionManagementService;
  readonly isSessionServerAdmin: boolean;
}

export interface GeneralSiteSettingsSubspaceServicesProps {
  readonly serverSettingsService: ServerSettings;
}

export type GeneralSiteSettingsSubspaceProps = GeneralSiteSettingsSubspaceOwnProps & GeneralSiteSettingsSubspaceServicesProps;

export interface GeneralSiteSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly serverSettings: IEditableServerSettings;
  readonly siteSettings: ISiteSettingsForSiteAdmin;
  readonly siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
  readonly siteAuthenticationSettings: ISiteAuthenticationSettings;
}

export class GeneralSiteSettingsSubspace extends React.PureComponent<GeneralSiteSettingsSubspaceProps, GeneralSiteSettingsSubspaceState> {

  private unmounted: boolean = false;

  private initialState: GeneralSiteSettingsSubspaceState = {
    hasDataToRender: false,
    hasError: false,
    serverSettings: undefined,
    siteSettings: undefined,
    siteAuthenticationSettings: undefined,
    siteSettingsForServerAdmin: undefined
  };

  public constructor(props: GeneralSiteSettingsSubspaceProps) {
    super(props);
    this.state = this.initialState;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: GeneralSiteSettingsSubspaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.updateState(this.initialState);
      this.getData();
    }
  }

  public render() {
    if (this.state.hasError) {
      return <RouterError errorType={'default'} urls={this.props.urls} />;
    }
    if (!this.state.hasDataToRender) {
      return null;
    }

    return (
      <div className={'tb-stylish-settings'}>
        <GeneralSiteSettingsCoordinator {...this.state}/>
      </div>
    );
  }

  private updateState<K extends keyof GeneralSiteSettingsSubspaceState>(newState: Pick<GeneralSiteSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  /**
   * Load all three settings bundles concurrently and commit them in a single
   * setState. Site-admin, server-admin, and auth settings each have their own
   * loader so we can independently stub responses for users who can't call the
   * underlying endpoint. All three must resolve before we flip hasDataToRender,
   * so the page either shows the full form or the loading state — never a
   * half-populated form.
   */
  private getData = async (): Promise<void> => {
    try {
      const [serverBundle, siteSettings, siteAuthenticationSettings] = await Promise.all([
        this.loadSettingsForServerAdmin(),
        this.loadSettingsForSiteAdmin(),
        this.loadSiteAuthenticationSettings(),
      ]);
      this.updateState({
        ...serverBundle,
        siteSettings,
        siteAuthenticationSettings,
        hasDataToRender: true,
      });
    } catch (e) {
      this.updateState({ hasError: true, hasDataToRender: true });
    }
  };

  private loadSettingsForServerAdmin = async (): Promise<{
    serverSettings: IEditableServerSettings | null;
    siteSettingsForServerAdmin: ISiteSettingsForServerAdmin | null;
  }> => {
    if (!this.props.isSessionServerAdmin) {
      return { serverSettings: null, siteSettingsForServerAdmin: null };
    }
    const serverSettings = await this.props.serverSettingsService.fetch();
    await this.props.server.getSessionInfoPromise();
    const siteSettingsForServerAdmin = await Sites.instance.getCurrentSiteSettingsForServerAdmin();
    this.copyServerSettingsToSite(siteSettingsForServerAdmin, serverSettings);
    return { serverSettings, siteSettingsForServerAdmin };
  };

  private loadSettingsForSiteAdmin = async (): Promise<ISiteSettingsForSiteAdmin> => {
    // Admin Profile users can't call the legacy /sites endpoint (403). Substitute
    // a metadata-driven stub so the form still mounts; each section loads its
    // own values from the V2 metadata API. Gated on adminProfilesForSiteSettingsEnabled
    // so behavior for non-Admin-Profile users is byte-identical to master when the
    // flag is off.
    if (FeatureFlags.adminProfilesForSiteSettingsEnabled
        && !this.props.server.isSessionSiteAdmin()
        && !this.props.server.isSessionServerAdmin()) {
      return this.loadMetadataDrivenSiteSettingsStub();
    }
    return this.loadLegacySiteSettingsForSiteAdmin();
  };

  /**
   * Legacy path: call the /sites endpoint that populates the full ISiteAdminSettings
   * shape. Used by Server and Site Admins.
   */
  private loadLegacySiteSettingsForSiteAdmin = async (): Promise<ISiteSettingsForSiteAdmin> => {
    await this.props.server.getSessionInfoPromise();
    return Sites.instance.getCurrentSiteSettingsForSiteAdmin();
  };

  /**
   * Metadata-driven stub for Admin Profile users. The /sites endpoint would 403
   * for them, so we return a minimal shape with an empty siteSettingsV2 bag.
   * Each rendered section populates its own values via React Query
   * (see MetadataDrivenSiteSetting).
   */
  private loadMetadataDrivenSiteSettingsStub = (): ISiteSettingsForSiteAdmin => ({
    siteAdminSettings: { siteSettingsV2: {} },
    siteId: '',
  });

  private loadSiteAuthenticationSettings = async (): Promise<ISiteAuthenticationSettings> => {
    // Admin Profile users don't have permission to call getSiteAuthenticationSettings and
    // don't render any auth settings sections, so return an empty stub.
    // Gated on adminProfilesForSiteSettingsEnabled for the same rollback-safety reason
    // as loadSettingsForSiteAdmin above.
    if (FeatureFlags.adminProfilesForSiteSettingsEnabled
        && !this.props.server.isSessionSiteAdmin()
        && !this.props.server.isSessionServerAdmin()) {
      return {} as ISiteAuthenticationSettings;
    }
    return SiteAuthenticationSettingsService.getSiteAuthenticationSettings(/* refresh */ true);
  };

  private copyServerSettingsToSite(siteSettings: ISiteSettingsForServerAdmin, serverSettings: IEditableServerSettings)
  {
    // copy server-wide settings that are necessary for site-specific sections
    siteSettings.settings.guestEnabledOnServer = serverSettings.guestEnabled;
  }
}
